import { useEffect } from "react";
import { CaretDown, Check, Lightning, CurrencyDollar } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSeparator,
  DropdownLabel,
} from "../ui/Dropdown";
import { useAIStore } from "../../stores/ai";
import { cn } from "../../lib/cn";
import type { ProviderInfo, ModelInfo } from "../../types/ai";

const PROVIDER_LABELS: Record<string, string> = {
  gemini: "Gemini",
  xai: "Grok",
  cerebras: "Cerebras",
  groq: "Groq",
  openai: "OpenAI",
  anthropic: "Anthropic",
};

function TierBadge({ tier }: { tier: ModelInfo["tier"] }) {
  if (tier === "free") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5",
          "text-[9px] font-semibold uppercase tracking-wider leading-none",
          "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)]",
        )}
      >
        <Lightning size={8} weight="fill" />
        Free
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5",
        "text-[9px] font-semibold uppercase tracking-wider leading-none",
        "bg-[var(--color-ember-orange)]/10 text-[var(--color-ember-orange)]",
      )}
    >
      <CurrencyDollar size={8} weight="bold" />
      Paid
    </span>
  );
}

function ProviderGroup({
  provider,
  selectedModel,
  onSelect,
}: {
  provider: ProviderInfo;
  selectedModel: string | null;
  onSelect: (providerId: string, modelId: string) => void;
}) {
  return (
    <>
      <DropdownLabel>{PROVIDER_LABELS[provider.id] ?? provider.name}</DropdownLabel>
      {provider.models.map((model) => {
        const isActive = selectedModel === model.id;
        return (
          <DropdownItem
            key={model.id}
            onClick={() => onSelect(provider.id, model.id)}
            className="justify-between"
          >
            <span className="flex items-center gap-2">
              <span className={cn(isActive ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]")}>
                {model.name}
              </span>
              <TierBadge tier={model.tier} />
            </span>
            <AnimatePresence>
              {isActive && (
                <motion.span
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="text-[var(--color-signal-green)]"
                >
                  <Check size={14} weight="bold" />
                </motion.span>
              )}
            </AnimatePresence>
          </DropdownItem>
        );
      })}
    </>
  );
}

export function ModelSelector({ className }: { className?: string }) {
  const availableProviders = useAIStore((s) => s.availableProviders);
  const selectedModel = useAIStore((s) => s.selectedModel);
  const setProvider = useAIStore((s) => s.setProvider);
  const fetchModels = useAIStore((s) => s.fetchModels);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const activeModelName = (() => {
    for (const p of availableProviders) {
      const m = p.models.find((m) => m.id === selectedModel);
      if (m) return m.name;
    }
    return selectedModel ?? "Select model";
  })();

  const freeProviders = availableProviders.filter((p) =>
    p.models.some((m) => m.tier === "free"),
  );
  const paidProviders = availableProviders.filter((p) =>
    p.models.every((m) => m.tier === "paid"),
  );

  if (availableProviders.length === 0) {
    return (
      <span
        className={cn(
          "flex items-center gap-1.5 rounded-[var(--radius-md)] px-2 py-1",
          "text-xs font-medium text-[var(--text-muted)]",
          className,
        )}
        title="No AI providers configured — add an API key in Settings"
      >
        <Lightning size={12} weight="regular" className="opacity-50" />
        <span className="max-w-[120px] truncate">No model</span>
      </span>
    );
  }

  return (
    <Dropdown>
      <DropdownTrigger
        className={cn(
          "flex items-center gap-1.5 rounded-[var(--radius-md)] px-2 py-1",
          "text-xs font-medium text-[var(--text-secondary)]",
          "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
          "active:scale-[0.97]",
          "transition-all duration-[var(--duration-fast)]",
          className,
        )}
      >
        <span className="max-w-[120px] truncate">{activeModelName}</span>
        <CaretDown size={10} weight="bold" className="shrink-0 opacity-50" />
      </DropdownTrigger>
      <DropdownMenu align="end" className="w-64">
        {freeProviders.map((provider, i) => (
          <div key={provider.id}>
            {i > 0 && <DropdownSeparator />}
            <ProviderGroup
              provider={provider}
              selectedModel={selectedModel}
              onSelect={setProvider}
            />
          </div>
        ))}
        {paidProviders.length > 0 && freeProviders.length > 0 && (
          <DropdownSeparator />
        )}
        {paidProviders.map((provider, i) => (
          <div key={provider.id}>
            {i > 0 && <DropdownSeparator />}
            <ProviderGroup
              provider={provider}
              selectedModel={selectedModel}
              onSelect={setProvider}
            />
          </div>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
